/**
 * @file `GET /families/{familyId}/study/exploration` のクライアント。
 * @what: 習い事・体験の「いまの状況」スナップショット (家族単位・読み取り専用)。
 * @why: 正本は and-lives (And Lives の「道すじ」)。いちごスタディはその最新状況を重ねて
 *   見せるだけで、書き込み API は無い (同期は CLI)。
 * @failure: GET は未同期なら 404 → `null` を返す (セクションごと非表示にする)。他は ApiError。
 */

import { ApiError, apiFetch } from "@/shared/api/client";

export interface StudyExplorationItem {
  label: string;
  detail?: string;
}

export interface StudyExplorationCluster {
  label: string;
  emoji?: string;
  items: StudyExplorationItem[];
}

export interface StudyExplorationAction {
  tag?: string;
  title: string;
}

export interface StudyExploration {
  schema_v: number;
  source_updated?: string;
  site_url?: string;
  clusters: StudyExplorationCluster[];
  actions: StudyExplorationAction[];
  created_at: string;
  updated_at: string;
}

export async function fetchStudyExploration(
  familyId: string,
  signal?: AbortSignal,
): Promise<StudyExploration | null> {
  try {
    return await apiFetch<StudyExploration>(
      `/families/${encodeURIComponent(familyId)}/study/exploration`,
      signal ? { signal } : {},
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw err;
  }
}

/**
 * "2026-08-11" → "2026年8月11日"。形が違えばそのまま返す。
 *
 * @why: `new Date("YYYY-MM-DD")` は UTC 解釈なので、JST 表示に Date / Intl を挟むと
 *   日付が 1 日ずれうる。値は日付文字列のままの文字列整形で完結させる (意図的に手動)。
 */
export function formatSourceUpdated(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return ymd;
  return `${m[1]}年${Number(m[2])}月${Number(m[3])}日`;
}
